import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import ReactSelect from 'react-select';
import { FaPlus, FaTimes, FaPenAlt, FaExclamationTriangle, FaCheckCircle, FaUndo, FaMinus } from 'react-icons/fa';

const ExpenseSubcategories = () => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [subcats, setSubcats] = useState([]);
    const [categories, setCategories] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [analysisCodes, setAnalysisCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState('');
    const [subcategoryFilter, setSubcategoryFilter] = useState('');

    const [showDialog, setShowDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        category_id: '',
        code: '',
        name: '',
        description: '',
        debit_account_code: '',
        gl_analysis_code: '',
        gl_notes: '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '42px',
            borderRadius: '0.375rem',
            borderColor: state.isFocused ? '#FF5733' : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(255,87,51,0.12)' : 'none',
            '&:hover': { borderColor: '#FF5733' }
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 30
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 50
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#fff7ed' : state.isSelected ? '#ffedd5' : '#fff',
            color: '#1f2937'
        })
    };

    const categoryOptions = categories.map((cat) => ({
        value: String(cat.id),
        label: `${cat.code} - ${cat.name}`
    }));

    const glAccountOptions = glAccounts.map((account) => ({
        value: account.account_code,
        label: `${account.account_code} - ${account.account_name}`
    }));

    const analysisCodeOptions = analysisCodes.map((code) => ({
        value: code.code,
        label: `${code.code} - ${code.description}`
    }));

    const subcategoryOptions = useMemo(() => {
        return subcats.map((subcat) => ({
            value: String(subcat.id),
            label: subcat.code ? `${subcat.code} - ${subcat.name || ''}` : (subcat.name || '')
        }));
    }, [subcats]);

    const filteredSubcats = useMemo(() => {
        return subcats.filter((subcat) => {
            const matchesCategory = !categoryFilter || String(subcat.category_id) === String(categoryFilter);
            const matchesSubcategory = !subcategoryFilter || String(subcat.id) === String(subcategoryFilter);
            return matchesCategory && matchesSubcategory;
        });
    }, [subcats, categoryFilter, subcategoryFilter]);

    const selectedCategoryOption = categoryOptions.find((option) => option.value === String(categoryFilter)) || null;
    const selectedSubcategoryOption = subcategoryOptions.find((option) => option.value === String(subcategoryFilter)) || null;
    const filteredCountLabel = `${filteredSubcats.length} shown | ${subcats.length} total | ${subcats.filter(s => s.debit_account_code).length} GL-mapped`;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        setLoading(true);
        await Promise.all([fetchCategories(), fetchSubcategories(), fetchGlAccounts(), fetchAnalysisCodes()]);
        setLoading(false);
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/expenses/categories`, { headers: { Authorization: `Bearer ${authToken}` } });
            if (res.data.success) setCategories(res.data.data || []);
        } catch (e) { console.error(e); }
    };

    const fetchSubcategories = async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/expenses/sub-categories`, { headers: { Authorization: `Bearer ${authToken}` } });
            if (res.data.success) {
                const data = (res.data.data || []).slice();
                data.sort((a, b) => ((a.code || '') > (b.code || '')) ? 1 : ((a.code || '') < (b.code || '')) ? -1 : 0);
                setSubcats(data);
            }
        } catch (e) { console.error(e); }
    };

    const fetchGlAccounts = async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/expenses/gl-accounts`, { headers: { Authorization: `Bearer ${authToken}` } });
            if (res.data.success) setGlAccounts(res.data.data || []);
        } catch (e) { console.error(e); }
    };

    const fetchAnalysisCodes = async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/owner-analysis-codes`, { headers: { Authorization: `Bearer ${authToken}` } });
            if (res.data.success) setAnalysisCodes(res.data.data || []);
        } catch (e) { console.error(e); }
    };

    const getSaveErrorMessage = (err) => {
        const data = err?.response?.data;

        if (typeof data?.message === 'string' && data.message.trim()) {
            return data.message;
        }

        if (typeof data?.error === 'string' && data.error.trim()) {
            return data.error;
        }

        if (data?.errors) {
            if (typeof data.errors === 'string') {
                return data.errors;
            }

            if (Array.isArray(data.errors)) {
                const messages = data.errors.filter(Boolean).map(String);
                if (messages.length > 0) return messages.join(', ');
            }

            if (typeof data.errors === 'object') {
                const messages = Object.values(data.errors)
                    .flatMap((value) => {
                        if (Array.isArray(value)) return value;
                        return value ? [value] : [];
                    })
                    .filter(Boolean)
                    .map(String);

                if (messages.length > 0) {
                    return messages.join(', ');
                }
            }
        }

        return err?.message || 'Save failed';
    };

    const handleCategoryChange = (categoryId) => {
        const selectedCategory = categories.find((cat) => String(cat.id) === String(categoryId));
        const prefix = selectedCategory?.code ? `${String(selectedCategory.code).toUpperCase()}-` : '';

        setForm((prev) => ({
            ...prev,
            category_id: categoryId,
            code: prefix,
        }));
    };

    const openCreate = () => {
        setEditing(null);
        setError(null);
        const defaultCategory = categories[0];
        setForm({
            category_id: defaultCategory?.id || '',
            code: defaultCategory?.code ? `${String(defaultCategory.code).toUpperCase()}-` : '',
            name: '',
            description: '',
            debit_account_code: '',
            gl_analysis_code: '',
            gl_notes: '',
        });
        setShowDialog(true);
    };

    const openEdit = (s) => {
        setEditing(s);
        setError(null);
        setForm({
            category_id: s.category_id,
            code: s.code,
            name: s.name,
            description: s.description || '',
            debit_account_code: s.debit_account_code || '',
            gl_analysis_code: s.gl_analysis_code || '',
            gl_notes: s.gl_mapping_notes || '',
        });
        setShowDialog(true);
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError(null);
        try {
            let savedId;

            if (editing) {
                const res = await axios.put(
                    `${rootUrl}/api/expenses/sub-categories/${editing.id}`,
                    { name: form.name, description: form.description },
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
                if (!res.data.success) throw new Error(res.data.message);
                savedId = editing.id;
            } else {
                const res = await axios.post(
                    `${rootUrl}/api/expenses/sub-categories`,
                    { category_id: form.category_id, code: form.code, name: form.name, description: form.description },
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
                if (!res.data.success) throw new Error(res.data.message || JSON.stringify(res.data.errors));
                savedId = res.data.data.id;
            }

            // Save GL mapping if a debit account was selected
            if (form.debit_account_code) {
                await axios.put(
                    `${rootUrl}/api/expenses/sub-categories/${savedId}/gl-mapping`,
                    { debit_account_code: form.debit_account_code, analysis_code: form.gl_analysis_code || null, notes: form.gl_notes || null },
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
            }

            await fetchSubcategories();
            setShowDialog(false);
        } catch (e) {
            setError(getSaveErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (item) => {
        try {
            await axios.put(
                `${rootUrl}/api/expenses/sub-categories/${item.id}`,
                { is_active: !item.is_active },
                { headers: { Authorization: `Bearer ${authToken}` } }
            );
            fetchSubcategories();
        } catch (e) { console.error(e); }
    };

    const unmappedCount = subcats.filter(s => s.is_active && !s.debit_account_code).length;
    const clearFilters = () => {
        setCategoryFilter('');
        setSubcategoryFilter('');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Expense Subcategories</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">Manage subcategories and their GL account mappings</p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">

                {/* GL mapping health banner */}
                {unmappedCount > 0 && (
                    <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                        <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-amber-800 font-semibold text-sm">
                                {unmappedCount} active subcategor{unmappedCount === 1 ? 'y has' : 'ies have'} no GL mapping
                            </p>
                            <p className="text-amber-700 text-xs mt-0.5">
                                Expenses in these subcategories will fail validation when posting to GL. Edit each subcategory to assign a debit account.
                            </p>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-lg font-semibold text-[#2a2e30]">Subcategory List</h2>
                            <p className="mt-0.5 text-sm text-gray-500">{filteredCountLabel}</p>
                        </div>
                        <button
                            onClick={openCreate}
                            className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-4 py-2"
                        >
                            <FaPlus className="text-xs" /> Add Subcategory
                        </button>
                    </div>

                    {loading ? (
                        <p className="text-center py-10 text-gray-500 text-sm">Loading...</p>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
                                <div className="w-full lg:w-80">
                                    <label className="mb-1 block text-xs font-semibold text-gray-600">Filter by Category</label>
                                    <ReactSelect
                                        options={categoryOptions}
                                        value={selectedCategoryOption}
                                        onChange={(option) => setCategoryFilter(option?.value || '')}
                                        isSearchable
                                        isClearable
                                        styles={selectStyles}
                                        placeholder="Search category..."
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div className="w-full lg:w-80">
                                    <label className="mb-1 block text-xs font-semibold text-gray-600">Filter by Subcategory</label>
                                    <ReactSelect
                                        options={subcategoryOptions}
                                        value={selectedSubcategoryOption}
                                        onChange={(option) => setSubcategoryFilter(option?.value || '')}
                                        isSearchable
                                        isClearable
                                        styles={selectStyles}
                                        placeholder="Search subcategory..."
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                        menuPosition="fixed"
                                    />
                                </div>
                                {(categoryFilter || subcategoryFilter) && (
                                    <button
                                        type="button"
                                        onClick={clearFilters}
                                        className="inline-flex w-full items-center justify-center gap-2 rounded-sm bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-200 lg:w-auto"
                                    >
                                        <FaUndo className="text-xs" />
                                        Clear Filters
                                    </button>
                                )}
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[960px] table-auto text-left text-xs">
                                    <thead>
                                        <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                                            <th className="p-2 text-left text-xs font-semibold leading-none text-blue-gray-700">Code</th>
                                            <th className="p-2 text-left text-xs font-semibold leading-none text-blue-gray-700">Name</th>
                                            <th className="p-2 text-left text-xs font-semibold leading-none text-blue-gray-700">Category</th>
                                            <th className="p-2 text-left text-xs font-semibold leading-none text-blue-gray-700">GL Debit Account</th>
                                            <th className="p-2 text-left text-xs font-semibold leading-none text-blue-gray-700">Analysis Code</th>
                                            <th className="p-2 text-center text-xs font-semibold leading-none text-blue-gray-700">Status</th>
                                            <th className="p-2 text-center text-xs font-semibold leading-none text-blue-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSubcats.map((c) => {
                                            return (
                                                <tr key={c.id} className="align-top border-b border-gray-100 hover:bg-[#fff8f5]">
                                                    <td className="p-2 font-mono text-xs uppercase text-slate-800">{c.code}</td>
                                                    <td className="p-2 text-slate-600">{c.name}</td>
                                                    <td className="p-2 text-slate-600">{c.category_name || '-'}</td>
                                                    <td className="p-2">
                                                        {c.debit_account_code ? (
                                                            <div className="flex items-center gap-1.5">
                                                                <FaCheckCircle className="text-green-500 text-xs flex-shrink-0" />
                                                                <span className="font-mono text-xs text-slate-700">{c.debit_account_code}</span>
                                                                <span className="max-w-[140px] truncate text-xs text-slate-500" title={c.debit_account_name}>
                                                                    - {c.debit_account_name}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1.5">
                                                                <FaExclamationTriangle className="text-amber-400 text-xs flex-shrink-0" />
                                                                <span className="text-xs font-medium text-amber-600">Not mapped</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="p-2">
                                                        {c.gl_analysis_code ? (
                                                            <span className="rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 font-mono text-xs text-blue-700">
                                                                {c.gl_analysis_code}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className="p-2 text-center">
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                            {c.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td className="p-2 text-center">
                                                        <div className="flex justify-center gap-1.5">
                                                            <button
                                                                title="Edit"
                                                                onClick={() => openEdit(c)}
                                                                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-blue-600 hover:bg-blue-50"
                                                            >
                                                                <FaPenAlt className="h-3 w-3" />
                                                            </button>
                                                            <button
                                                                title={c.is_active ? 'Deactivate' : 'Activate'}
                                                                onClick={() => toggleActive(c)}
                                                                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 text-sm font-bold"
                                                            >
                                                                {c.is_active ? <FaMinus className="h-3 w-3" /> : '+'}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {filteredSubcats.length === 0 && (
                                            <tr>
                                                <td colSpan={7} className="py-10 text-center text-sm text-gray-400">No subcategories found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit / Create Dialog */}
            {showDialog && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-[#2a2e30]">
                                {editing ? 'Edit Subcategory' : 'New Subcategory'}
                            </h3>
                            <button onClick={() => setShowDialog(false)} className="text-gray-400 hover:text-gray-600">
                                <FaTimes />
                            </button>
                        </div>

                        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">

                            {/* Subcategory fields */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Category <span className="text-red-500">*</span></label>                                <ReactSelect
                                    options={categoryOptions}
                                    value={categoryOptions.find((option) => option.value === String(form.category_id)) || null}
                                    onChange={(option) => handleCategoryChange(option?.value || '')}
                                    isDisabled={!!editing}
                                    isSearchable
                                    isClearable
                                    styles={selectStyles}
                                    placeholder="Select category"
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                />
                            </div>

                            {!editing && (
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Code <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={form.code}
                                        onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                        placeholder="Select a category to auto-fill prefix"
                                        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                    />
                                    <p className="text-xs text-gray-400 mt-0.5">Format: XXX-YYY (category code - subcategory code)</p>
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    placeholder="e.g. Regular Fuel"
                                    className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                                <input
                                    type="text"
                                    value={form.description}
                                    onChange={e => setForm({ ...form, description: e.target.value })}
                                    className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                />
                            </div>

                            {/* GL Mapping section */}
                            <div className="border-t pt-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <h4 className="text-sm font-semibold text-[#2a2e30]">GL Mapping</h4>
                                    {form.debit_account_code ? (
                                        <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                            <FaCheckCircle className="text-xs" /> Mapped
                                        </span>
                                    ) : (
                                            <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                            <FaExclamationTriangle className="text-xs" /> Not mapped - expenses cannot be posted to GL
                                        </span>
                                    )}
                                </div>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">GL Debit Account <span className="text-red-500">*</span></label>                                        <ReactSelect
                                            options={glAccountOptions}
                                            value={glAccountOptions.find((option) => option.value === form.debit_account_code) || null}
                                            onChange={(option) => setForm({ ...form, debit_account_code: option?.value || '' })}
                                            isSearchable
                                            isClearable
                                            styles={selectStyles}
                                            placeholder="Select GL account"
                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                            menuPosition="fixed"
                                        />
                                        <p className="text-xs text-gray-400 mt-0.5">The expense account debited when posting to GL</p>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Analysis Code</label>                                        <ReactSelect
                                            options={analysisCodeOptions}
                                            value={analysisCodeOptions.find((option) => option.value === form.gl_analysis_code) || null}
                                            onChange={(option) => setForm({ ...form, gl_analysis_code: option?.value || '' })}
                                            isSearchable
                                            isClearable
                                            styles={selectStyles}
                                            placeholder="None"
                                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                            menuPosition="fixed"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Mapping Notes</label>
                                        <input
                                            type="text"
                                            value={form.gl_notes}
                                            onChange={e => setForm({ ...form, gl_notes: e.target.value })}
                                            placeholder="Optional notes about this mapping"
                                            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                        />
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <p className="text-red-700 text-xs">{error}</p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 px-6 py-4 border-t">
                            <button
                                onClick={() => setShowDialog(false)}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-sm px-4 py-2"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#e64520] disabled:opacity-50 text-white text-sm font-semibold rounded-sm px-4 py-2"
                            >
                                <FaPlus className="text-xs" />
                                {saving ? 'Saving...' : editing ? 'Update' : 'Add Subcategory'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpenseSubcategories;

